import type { TipoInspecaoCasco } from "@/modules/obras/criar-obra.types";
import type { SelectOption } from "../../../nova-obra.constants";
import { INSPECAO_CASCO_VALUES } from "@/modules/obras/criar-obra.types";
import { ehDocagem, INSPECAO_CASCO_LABELS, TIPO_MOBILIZACAO } from "../../../hooks/nova-obra.tipos";
import { useCallback, useRef, useState } from "react";
import type { RefObject } from "react";
import { useMediaQuery } from "@/hooks";
import { TIPOS_A11Y } from "../../../nova-obra.constants";
import type { TipoObraCardProps } from "./types-tipo-obra-card";

const inspecaoOptions: SelectOption<TipoInspecaoCasco>[] = INSPECAO_CASCO_VALUES.map((value) => ({
  value,
  label: INSPECAO_CASCO_LABELS[value],
}));

export function useTipoObraCard({
  tipo,
  aberto,
  clientes,
  erroInspecao,
  dominante,
  prioridadeTexto,
  escopoMacro,
  onTornarDominante,
  onToggle,
  onRemover,
  onChange,
}: TipoObraCardProps) {
  const removerLabel = tipo.automatico ? TIPOS_A11Y.removerTipoAutomatico : TIPOS_A11Y.removerTipo;
  const idBase = `tipo-${tipo.uid}`;
  const infoRef = useRef<HTMLButtonElement>(null) as RefObject<HTMLButtonElement>;
  // No mobile o tooltip de prioridade (302px) abre acima do ⓘ; o PrimeReact ainda inverte e encaixa
  // na viewport se faltar espaço, e o `max-width` do módulo o limita à largura da tela.
  const mobile = useMediaQuery("(max-width: 768px)");
  const lixeiraRef = useRef<HTMLSpanElement>(null) as RefObject<HTMLSpanElement>;
  // A opção nunca fica marcada (só o modal confirmado efetiva): remontar o radio limpa o estado do DOM
  // que o PrimeReact deixa marcado após o clique, e assim ele reabre o modal em cliques seguintes.
  const [radioKey, setRadioKey] = useState(0);
  const tornarDominante = () => {
    setRadioKey((atual) => atual + 1);
    onTornarDominante();
  };
  // A lixeira não pode morar dentro do `<a role="button">` do cabeçalho do Prime (controle dentro de controle):
  // ela entra como irmã do link, no cabeçalho (portal), e o layout em flex do módulo a mantém onde o Figma a põe.
  const [cabecalhoEl, setCabecalhoEl] = useState<HTMLElement | null>(null);
  const codigo = tipo.codigo;
  const testIds = {
    card: `tipo-card-${codigo}`,
    cabecalho: `tipo-cabecalho-${codigo}`,
    seloDominante: `selo-dominante-${codigo}`,
    lixeira: `lixeira-${codigo}`,
    opcaoDominante: `opcao-dominante-${codigo}`,
    hintDominante: `hint-dominante-${codigo}`,
    cliente: `tipo-cliente-${codigo}`,
    inspecao: `tipo-inspecao-${codigo}`,
  };
  const registrarCabecalho = useCallback((el: HTMLElement | null) => setCabecalhoEl(el), []);
  const escolherCliente = (cliente: string | null | undefined) =>
    onChange({ cliente: cliente ?? "" });
  const alterarContrato = (contrato: string) => onChange({ contrato });
  const marcarInspecaoCasco = (marcado: boolean | undefined) =>
    onChange({
      haveraInspecaoCasco: marcado === true,
      inspecaoCasco: marcado ? tipo.inspecaoCasco : null,
    });
  const escolherInspecaoCasco = (valor: TipoInspecaoCasco | null | undefined) =>
    onChange({ inspecaoCasco: valor ?? null });

  return {
    removerLabel,
    idBase,
    infoRef,
    mobile,
    lixeiraRef,
    cabecalhoEl,
    registrarCabecalho,
    radioKey,
    tornarDominante,
    testIds,
    escolherCliente,
    alterarContrato,
    marcarInspecaoCasco,
    escolherInspecaoCasco,
    tipo,
    aberto,
    clientes,
    erroInspecao,
    dominante,
    prioridadeTexto,
    escopoMacro,
    onToggle,
    onRemover,
    ehDocagem: ehDocagem(tipo.codigo),
    ehMobilizacao: tipo.codigo === TIPO_MOBILIZACAO,
    inspecaoOptions,
  };
}
